import { useEffect } from 'react'

import { supabase } from '../../lib/supabase'

import { loadQueries } from './data'
import { SearchQuery } from './types'

// Setup realtime subscriptions for search queries
export function useRealtimeSubscriptionsEffect(
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setQueries: React.Dispatch<React.SetStateAction<SearchQuery[]>>,
  setRealtimeStatus: React.Dispatch<React.SetStateAction<'connecting' | 'connected' | 'disconnected'>>
) {
  useEffect(() => {
    let isMounted = true

    // Load queries on mount
    // useEffect with empty dependency array ensures this runs only once
    loadQueries(setLoading, setError, setQueries)

    const setupSubscriptions = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || !isMounted) return

      const channel = supabase
        .channel('search_queries_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'search_queries',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload: { eventType: string; new?: SearchQuery; old?: SearchQuery }) => {
            console.log('[SearchQueries] 🔔 Realtime update received:', payload.eventType, payload)
            
            if (payload.eventType === 'INSERT') {
              const newQuery = payload.new as SearchQuery
              const { count } = await supabase
                .from('failed_job_attempts')
                .select('*', { count: 'exact', head: true })
                .eq('search_query_id', newQuery.id)
              
              setQueries((prev) => {
                if (prev.some(q => q.id === newQuery.id)) {
                  return prev
                }
                return [{ ...newQuery, failed_jobs_count: count || 0 }, ...prev]
              })
            } else if (payload.eventType === 'UPDATE') {
              const updatedQuery = payload.new as SearchQuery
              
              setQueries((prev) => {
                const updated = prev.map((q) => 
                  q.id === updatedQuery.id 
                    ? { 
                        ...updatedQuery, 
                        failed_jobs_count: q.failed_jobs_count || 0
                      }
                    : q
                )
                
                const oldQuery = prev.find(q => q.id === updatedQuery.id)
                const lastScrapedChanged = oldQuery && oldQuery.last_scraped_at !== updatedQuery.last_scraped_at
                
                if (lastScrapedChanged) {
                  updated.sort((a, b) => {
                    const aTime = a.last_scraped_at ? new Date(a.last_scraped_at).getTime() : 0
                    const bTime = b.last_scraped_at ? new Date(b.last_scraped_at).getTime() : 0
                    if (bTime !== aTime) return bTime - aTime
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                  })
                }
                
                return updated
              })
            } else if (payload.eventType === 'DELETE') {
              const deletedId = payload.old.id
              setQueries((prev) => prev.filter((q) => q.id !== deletedId))
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'jobs',
            filter: `user_id=eq.${user.id}`,
          },
          (payload: { new: { search_query_id?: string } }) => {
            const newJob = payload.new
            if (newJob.search_query_id) {
              setQueries((prev) =>
                prev.map((q) =>
                  q.id === newJob.search_query_id
                    ? { ...q, scraped_jobs_count: (q.scraped_jobs_count || 0) + 1 }
                    : q
                )
              )
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'jobs',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const deletedJob = payload.old as { search_query_id?: string }
            if (deletedJob.search_query_id) {
              setQueries((prev) =>
                prev.map((q) =>
                  q.id === deletedJob.search_query_id
                    ? { ...q, scraped_jobs_count: Math.max(0, (q.scraped_jobs_count || 0) - 1) }
                    : q
                )
              )
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'failed_job_attempts',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const attempt = payload.eventType === 'DELETE' ? payload.old : payload.new
            if (attempt?.search_query_id) {
              const delta = payload.eventType === 'INSERT' ? 1 : payload.eventType === 'DELETE' ? -1 : 0
              setQueries((prev) =>
                prev.map((q) =>
                  q.id === attempt.search_query_id
                    ? { ...q, failed_jobs_count: Math.max(0, (q.failed_jobs_count || 0) + delta) }
                    : q
                )
              )
            }
          }
        )
        .subscribe((status: 'SUBSCRIBED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | 'CLOSED') => {
          if (process.env.NODE_ENV === 'development' && status !== 'CLOSED') {
            console.log('[SearchQueries] 📡 Realtime subscription status:', status)
          }
          if (status === 'SUBSCRIBED') {
            setRealtimeStatus('connected')
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setRealtimeStatus('disconnected')
          } else if (status === 'CLOSED') {
            // CLOSED is normal during cleanup, don't log it
            setRealtimeStatus('disconnected')
          } else {
            setRealtimeStatus('connecting')
          }
        })

      return () => {
        if (channel) {
          // Use unsubscribe instead of removeChannel to avoid WebSocket errors
          // unsubscribe gracefully closes the channel without throwing errors
          try {
            channel.unsubscribe()
          } catch (error) {
            // Ignore errors during cleanup - they're harmless
          }
          // Also remove channel from Supabase client to clean up resources
          try {
            supabase.removeChannel(channel)
          } catch (error) {
            // Ignore WebSocket errors during cleanup - they're harmless
            // WebSocket might already be closed or in a closing state
          }
        }
      }
    }

    const cleanup = setupSubscriptions()
    
    return () => {
      isMounted = false
      cleanup.then(fn => {
        if (fn) {
          // Execute cleanup function which will unsubscribe and remove channel
          try {
            fn()
          } catch (error) {
            // Ignore cleanup errors - WebSocket errors are harmless during unmount
          }
        }
      }).catch(() => {
        // Ignore cleanup promise errors
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Functions from useState are stable and don't need to be in dependencies
    // This effect should only run once on mount
  }, [])
}
